import type { ReactNode } from "react";
import Box from "@mui/material/Box";

const RICH_PART_SPLIT_REGEX =
  /(\{\{[^}]+\}\}|```[^`]+```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;

function renderVariableSpan(part: string, index: number): ReactNode {
  return (
    <Box
      component="span"
      key={`v-${index}-${part}`}
      sx={{
        color: "primary.main",
        fontWeight: 600,
        fontFamily: "monospace",
        fontSize: "0.82em",
      }}
    >
      {part}
    </Box>
  );
}

function renderRichText(text: string, keyPrefix: string): ReactNode[] {
  const segments: ReactNode[] = [];
  const parts = text.split(RICH_PART_SPLIT_REGEX);

  parts.forEach((part, index) => {
    if (!part) return;
    if (/^\{\{[^}]+\}\}$/.test(part)) {
      segments.push(renderVariableSpan(part, index));
      return;
    }
    if (/^\*[^*\n]+\*$/.test(part)) {
      segments.push(
        <Box component="strong" key={`${keyPrefix}-b-${index}`} sx={{ fontWeight: 700 }}>
          {part.slice(1, -1)}
        </Box>,
      );
      return;
    }
    if (/^_[^_\n]+_$/.test(part)) {
      segments.push(
        <Box component="em" key={`${keyPrefix}-i-${index}`} sx={{ fontStyle: "italic" }}>
          {part.slice(1, -1)}
        </Box>,
      );
      return;
    }
    if (/^~[^~\n]+~$/.test(part)) {
      segments.push(
        <Box
          component="span"
          key={`${keyPrefix}-s-${index}`}
          sx={{ textDecoration: "line-through" }}
        >
          {part.slice(1, -1)}
        </Box>,
      );
      return;
    }
    if (/^```[^`]+```$/.test(part)) {
      segments.push(
        <Box
          component="code"
          key={`${keyPrefix}-m-${index}`}
          sx={{
            fontFamily: "monospace",
            bgcolor: "rgba(0,0,0,0.06)",
            px: 0.35,
            borderRadius: 0.5,
          }}
        >
          {part.slice(3, -3)}
        </Box>,
      );
      return;
    }
    segments.push(<span key={`${keyPrefix}-t-${index}`}>{part}</span>);
  });

  return segments;
}

export function renderBodyRich(text: string): ReactNode {
  if (!text.trim()) return null;
  return renderRichText(text, "body");
}

export function renderHeaderText(text: string): ReactNode {
  if (!text.trim()) return null;
  return renderRichText(text, "header");
}

